// ═══════════════════════════════════════════════════════════════════════════════════════════
// SendSearchFilters — the filter bar (CONTRACT §4.2, `SendSearch-Mock-v3.html:172-198`).
//
// DELIVERY PATH: _delivery\SendSearch-V1\react\screens\SendSearch\components\SendSearchFilters.tsx
// TARGET PATH:   ReactCode\src\screens\SendSearch\components\SendSearchFilters.tsx
//
// Mock facts honoured exactly:
//   :176      free-text search — "שם, אימייל או סלולרי של סוכן או מפקח…"
//   :178-180  role select — סוכן ומפקח / סוכן בלבד / מפקח בלבד
//   :181      campaign select — FILTERS-2PICK 2026-10-02 (Idan): no longer in this row; it is the
//             first row of the "עוד מסננים" panel, as TWO pickers (e-mail / WhatsApp campaigns)
//   :182      two date fields
//   :183      "חפש" (primary) + "עוד מסננים ▾"  ← the mock's own is a stub; here it opens the panel
//   :187-191  segmented row-kind control — הכל / סוכנים בלבד / נמעני ריכוז בלבד
//   :194      "נקה הכל" link
//   :195-196  the >1-year opt-in checkbox, pinned to the far end of the row
//
// The type is imported as `Filters` because CONTRACT §4.3 names the state type `SendSearchFilters`
// and this file's default export has the same name — the alias keeps both, with no renaming of the
// contract type.
// ═══════════════════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import {
    Badge, Box, Button, ButtonGroup, Checkbox, Chip, Collapse, FormControlLabel, InputAdornment,
    Menu, MenuItem, Paper, TextField, Tooltip, Typography,
} from '@material-ui/core';
import { ExpandLess, ExpandMore, Search } from '@material-ui/icons';
// v4-alpha lab, the same source `GroupSelectorDropDown.tsx:4` uses. Deliberately NOT a
// `<Select multiple>`: a search box placed inside a MUI v4 Select's MenuList fights the list's
// built-in type-ahead, which swallows keystrokes and jumps the highlight on every letter typed.
// Autocomplete's input IS the search box, so there is no second keyboard consumer to fight.
import { Autocomplete } from '@material-ui/lab';
import CheckBoxOutlineBlankIcon from '@material-ui/icons/CheckBoxOutlineBlank';
import CheckBoxIcon from '@material-ui/icons/CheckBox';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
    SS,
    SendSearchFilters as Filters,
    SendSearchCampaign,
    SendSearchCatalogSource,
    SendSearchSourceCampaign,
    CAMPAIGN_IDS_MAX,
    eRowKind,
    eSendSearchChannel,
    SEND_SEARCH_CHANNELS,
    channelFilterKey,
} from '../../../Models/DataSources/SendSearch';
import AdvancedFilterBuilder from './AdvancedFilterBuilder';
import {
    SendSearchField,
    SendSearchFilterRule,
    SendSearchSort,
    completeRules,
    defaultSendSearchSort,
    fieldByKey,
} from './SendSearchAdvanced';

// QA BUG-05-r2: how long a date field must be quiet (no input, no key) before its value is committed.
// Long enough for the two digits of a day / month typed in a row; Enter commits at once (blur does not:
// QA BUG-05-r3).
const DATE_SETTLE_MS = 800;
// QA BUG-05-r4: while a pointer is pressed, a settled date waits in steps of DATE_PRESS_STEP_MS (at most
// DATE_PRESS_MAX_MS in all, so a lost pointerup can never hold a date for good) — see `armDate`.
const DATE_PRESS_STEP_MS = 50;
const DATE_PRESS_MAX_MS = 2000;

// The campaign pickers' options: one entry per SEND from the catalog SP's result set [1] (see
// `SendSearchCampaign` for every field). FILTERS-2PICK 2026-10-02: an alias, no longer a copy of
// that interface — a second declaration had to be kept in step by hand, and the four new label
// fields (EmailCampaignName / WaUnitName and their ids) would have been one more place to forget.
// `CampaignID` is the SEND KEY (negative for a WhatsApp-only send): identity; on screen only as the
// `#id` fallback of a send the list cannot name.
export type CampaignOption = SendSearchCampaign;

interface Props {
    value: Filters;
    // Partial patch, committed to the slice by the screen. Everything except the free-text box is a
    // COMMITTED filter (it refetches immediately) — matching the mock, where the selects call
    // applyF() directly and only the text box waits for "חפש".
    onChange: (patch: Partial<Filters>) => void;
    onSearch: () => void;
    onClearAll: () => void;
    campaigns: CampaignOption[];
    // Distinguishes "the server could not give us the list" from "the list is genuinely empty".
    // Without it the picker's empty state would assert, from a failed request, that this account has
    // no campaigns — a claim about the DATA made from a fact about the NETWORK.
    campaignsError?: string | null;
    loading?: boolean;
    // The campaign list has not arrived yet (review round 3): the catalog waits for the first search,
    // and an empty picker in that window must say "loading", never "no campaign found".
    campaignsLoading?: boolean;

    // ── "mark the campaigns that sent from source X" (script 54) ─────────────────────────────
    // A MOMENTARY ACTION, not a filter. It ticks campaigns into the campaign pickers and then has no
    // further existence: no value is kept, nothing is added to `Filters`, nothing new goes on the
    // wire. That is precisely why this feature needs no SP parameter and cannot 500 a search.
    //
    // `sourceMapAvailable` is the deployment gate and the server derives it from TABLE PRESENCE,
    // not from the lists being non-empty. False ⇒ the button is not rendered AT ALL. A control
    // that is visible but silently ticks nothing is the RoleFilter defect this screen deleted once
    // already (see the note at the role ButtonGroup below).
    sourceMapAvailable?: boolean;
    sourceOptions?: SendSearchCatalogSource[];
    sourceCampaigns?: SendSearchSourceCampaign[];

    // ── advanced filter builder (CONTRACT §2) ────────────────────────────────────────────────
    // ALL OPTIONAL, and that is the degrade path, not laziness: against a server that does not yet
    // project the searchable-field list, `fields` is undefined ⇒ the builder is NOT rendered —
    // CONTRACT §2: a builder over an empty field list can express nothing. FILTERS-2PICK
    // 2026-10-02: the "עוד מסננים" panel still opens for the campaign pickers (its first row);
    // the button is disabled only when neither part has anything to show (see `panelAvailable`).
    fields?: SendSearchField[];
    rules?: SendSearchFilterRule[];
    onRulesChange?: (rules: SendSearchFilterRule[]) => void;
    sort?: SendSearchSort;
    onSortChange?: (sort: SendSearchSort) => void;

    // ── 11-sendsearch §8 ──────────────────────────────────────────────────────────────────────
    // The channel segment is rendered ONLY when the server says the two-channel search is ready.
    // Hidden, not disabled: on a not-ready API the value would be mapped to e-mail anyway, so a
    // visible וואטסאפ button would be a control that silently does nothing.
    channelsReady?: boolean;
    // The rules/sort the grid is ACTUALLY filtered by — what the last request carried. The builder
    // edits `rules`/`sort`; the badge and the collapsed chips describe THESE, because a chip for a
    // rule that was never applied tells the operator the grid is narrowed by something it is not.
    appliedRules?: SendSearchFilterRule[];
    appliedSort?: SendSearchSort;
    // Commit the builder's rules + sort (its "החל מסננים"). Falls back to `onSearch`.
    onApply?: () => void;
    // Chip delete: drop ONE applied rule / the applied sort and re-search, without committing any
    // other unapplied edit that happens to be sitting in the builder.
    onRemoveRule?: (id: string) => void;
    onClearSort?: () => void;
}

const KIND_ORDER: eRowKind[] = [eRowKind.All, eRowKind.Agents, eRowKind.Rollup];
const KIND_KEY: { [k in eRowKind]: string } = {
    [eRowKind.All]: 'kind.all',
    [eRowKind.Agents]: 'kind.agents',
    [eRowKind.Rollup]: 'kind.rollup',
};

// ── one campaign picker (FILTERS-2PICK 2026-10-02) ───────────────────────────────────────────────
// The checkbox picker that used to sit in the top row, unchanged in behaviour, and rendered once per
// channel (e-mail, WhatsApp) — or once, for a server that is not two-channel ready. It holds NO
// state: `value` is derived from `CampaignIDs` by the parent and `onPick` merges back into it, so
// the two pickers are two views of ONE selection and cannot disagree.
//
// Facts carried over from the single picker:
//   • NO "all" sentinel: an EMPTY selection already means "all campaigns", both on the wire and in
//     the SP (`@HasCampFilter = 0` ⇒ the predicate is inert). The placeholder says so instead.
//   • `CampaignID` (scalar) in the slice is left untouched at its default null — the SP unions the
//     two, so writing both would be two sources of truth for one filter.
//   • The options are the SERVER's list (result set [1] of the catalog SP), never campaigns scraped
//     from loaded rows, so "not found" means the send does not exist in scope, not "not fetched yet".
interface SendPickerProps {
    label: string;
    placeholder: string;
    noOptionsText: string;
    options: CampaignOption[];
    value: CampaignOption[];
    labelOf: (o: CampaignOption) => string;
    // The channels the SEND has, from the server's flags only ('' when they did not arrive).
    markerOf: (o: CampaignOption) => string;
    onPick: (next: CampaignOption[]) => void;
    isRTL: boolean;
}

const SendPicker: React.FC<SendPickerProps> = ({
    label, placeholder, noOptionsText, options, value, labelOf, markerOf, onPick, isRTL,
}) => (
    <Autocomplete
        multiple
        disableCloseOnSelect
        size="small"
        options={options}
        value={value}
        onChange={(_e: any, next: any) => onPick((next || []) as CampaignOption[])}
        // A send with no name still has to be pickable and, more importantly, READABLE once
        // picked — an unlabelled chip is a filter the user cannot identify (`labelOf` ends in `#id`).
        getOptionLabel={(o: any) => (o ? labelOf(o as CampaignOption) : '')}
        // Identity is the SEND KEY, never the object: the parent rebuilds `value` on every render,
        // so reference equality would tick nothing.
        getOptionSelected={(o: any, v: any) => !!o && !!v && o.CampaignID === v.CampaignID}
        // Beyond two, chips eat the whole row. "+N" stays clickable and the full list is one click
        // away in the open menu; with the panel closed, the chips row under the bar lists them all.
        limitTags={2}
        // The menu is portaled out of App's inner <div dir>, and <html dir> is stuck "ltr" — forced
        // on the Paper, as the parent does for its other popups.
        PaperComponent={(props: any) => <Paper {...props} dir={isRTL ? 'rtl' : 'ltr'} />}
        noOptionsText={noOptionsText}
        // QA BUG-21: no maxWidth. The panel row is exactly as wide as row 1 (see the box around row 1), so
        // two pickers with the same basis and grow split it equally and the second ends under «עוד מסננים».
        style={{ minWidth: 260, flex: '1 1 320px' }}
        renderOption={(option: any, { selected }: any) => {
            const marker = option ? markerOf(option as CampaignOption) : '';
            return (
                <>
                    <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        style={{ marginInlineEnd: 8 }}
                        checked={selected}
                        color="primary"
                    />
                    <Typography component="span" style={{ fontSize: 13.5, flex: '1 1 auto', minWidth: 0 }}>
                        {option ? labelOf(option as CampaignOption) : ''}
                    </Typography>
                    {/* 11-sendsearch §8: which channels this SEND has. In the WhatsApp picker a
                        combined send reads "אימייל · וואטסאפ", which is what tells the user that
                        ticking it here ticks it in the e-mail picker too. */}
                    {!!marker && (
                        <Typography
                            component="span"
                            style={{
                                flex: '0 0 auto', fontSize: 11, color: 'var(--pls-d-text-2, #5b6b7b)', marginInlineStart: 8,
                                border: '1px solid #d0d7de', borderRadius: 3, padding: '0 5px', whiteSpace: 'nowrap',
                            }}
                        >
                            {marker}
                        </Typography>
                    )}
                </>
            );
        }}
        renderInput={(params: any) => (
            <TextField
                {...params}
                variant="outlined"
                label={label}
                InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                // The placeholder carries the state: with nothing ticked in EITHER picker the filter
                // IS "all", and saying so is what replaces the removed "all" menu item.
                placeholder={placeholder}
                inputProps={{
                    ...params.inputProps,
                    'aria-label': label,
                    autoComplete: 'off',
                }}
            />
        )}
    />
);

const SendSearchFilters: React.FC<Props> = ({
    value, onChange, onSearch, onClearAll, campaigns, campaignsError, loading, campaignsLoading,
    sourceMapAvailable, sourceOptions, sourceCampaigns,
    fields, rules, onRulesChange, sort, onSortChange,
    channelsReady, appliedRules, appliedSort, onApply, onRemoveRule, onClearSort,
}) => {
    const { t } = useTranslation();
    // MUI portals its popups to document.body — outside App's inner <div dir> — and <html dir> is
    // stuck "ltr", so any menu opens LTR unless its own Paper is given a direction. Passed to each
    // SendPicker's Autocomplete PaperComponent, exactly as BusinessColumnsPicker (SmartSend) does for a
    // Select. isRTL is the same redux source App uses for the body div.
    const isRTL = useSelector((s: any) => s.core && s.core.isRTL);

    // ── campaign pickers: ONE selection, TWO views (FILTERS-2PICK 2026-10-02) ─────────────────
    // `CampaignIDs` is unchanged on the wire: a list of SEND KEYS — positive = the e-mail CampaignID
    // of a send that has e-mail (a combined send included), negative = -WACampaignID of a
    // WhatsApp-only send. The e-mail picker and the WhatsApp picker are two VIEWS of that one list,
    // derived here on every render — no second piece of state that could disagree with the first.
    // A combined send is an option of BOTH pickers under the SAME key, so ticking it in one ticks it
    // in the other and un-ticking it in either removes it: one row, one send.
    //
    // Two pickers only when the server says the search is two-channel ready AND the catalog sent
    // the channel flags on every send (the _Stage catalog). Otherwise (base catalog ⇒ flags null)
    // every campaign goes to ONE picker under its CampaignName — the pre-2PICK behaviour — because
    // splitting the list by a flag the server did not send would be a guess.
    const selectedIds: number[] = value.CampaignIDs || [];
    // `length > 0`: `every` is true for an empty list, and an empty (loading / failed / no sends)
    // catalog has sent no flags at all — it must not switch on the two-picker texts.
    const twoPickers = channelsReady === true && campaigns.length > 0
        && campaigns.every((c) => c.HasEmail != null && c.HasWhatsApp != null);
    const emailOptions: CampaignOption[] = twoPickers ? campaigns.filter((c) => c.HasEmail === true) : campaigns;
    const waRaw: CampaignOption[] = twoPickers ? campaigns.filter((c) => c.HasWhatsApp === true) : [];
    // First occurrence wins — the same answer the old `filter(...)[0]` lookup gave, without a scan
    // of the whole catalog per selected id (up to CAMPAIGN_IDS_MAX of them) on every render.
    const campaignById = new Map<number, CampaignOption>();
    campaigns.forEach((c) => { if (!campaignById.has(c.CampaignID)) campaignById.set(c.CampaignID, c); });

    // Labels. Each picker names the send by ITS channel's object: the e-mail campaign's name, or the
    // WhatsApp unit's name / `#unit id`. A blank name ends in `#id` of that picker's id space; the
    // one exception is the last arm of `waLabelOf` below (a combined send for which the server sent
    // no unit id and no name at all), where the send's key is the only identifier left.
    const emailLabelOf = (o: CampaignOption): string => {
        const name = twoPickers ? (o.EmailCampaignName ?? o.CampaignName) : o.CampaignName;
        return name ? name : `#${o.CampaignID}`;
    };
    const waLabelOf = (o: CampaignOption): string => {
        const name = o.WaUnitName ?? (o.HasEmail === false ? o.CampaignName : null);
        if (name) return name;
        if (o.WACampaignID != null) return `#${o.WACampaignID}`;
        // No unit name and no unit id: a send key the catalog does not list (negative ⇒ the unit id
        // IS -key), or a combined send against an API/catalog without the 2PICK tail, whose only
        // name is the send's own CampaignName.
        return o.CampaignID < 0 ? `#${-o.CampaignID}` : (o.CampaignName || `#${o.CampaignID}`);
    };
    // The catalog orders by CampaignName — the E-MAIL name of a combined send — so the WhatsApp
    // picker re-sorts by the label it actually shows (the unit's name).
    const waOptions: CampaignOption[] = [...waRaw].sort((a, b) => waLabelOf(a).localeCompare(waLabelOf(b)));
    // The channels a SEND has, from the server's flags only. A catalog that is not ready sends
    // neither, and a guessed marker would be a claim — so '' there, never a default.
    const markerOf = (o: CampaignOption): string => [
        o.HasEmail === true ? t(channelFilterKey(eSendSearchChannel.EMAIL)) : null,
        o.HasWhatsApp === true ? t(channelFilterKey(eSendSearchChannel.WHATSAPP)) : null,
    ].filter((v) => !!v).join(' · ');

    // 🔴 THE FALLBACK IS THE POINT. An id whose option is not in the list — the catalog has not
    // loaded yet, or the user narrowed the date range so the campaign left scope — still produces a
    // chip, labelled `#id`. Silently dropping it would leave the grid filtered by a campaign with
    // NOTHING on screen to say so: the user would see a near-empty result, no chips, and conclude
    // that nothing was sent. Showing an ugly `#4821` chip they can remove is strictly better than
    // hiding an active filter, and it is the same principle as the advanced-rule chips below.
    // With two pickers the key's SIGN picks the picker: positive = an e-mail CampaignID (e-mail
    // picker, `#id`), negative = a WhatsApp-only send (WhatsApp picker, `#` + the unit id).
    const emailValue: CampaignOption[] = [];
    const waValue: CampaignOption[] = [];
    selectedIds.forEach((id) => {
        const c = campaignById.get(id);
        const inEmail = !!c && (!twoPickers || c.HasEmail === true);
        const inWa = !!c && twoPickers && c.HasWhatsApp === true;
        if (inEmail) emailValue.push(c as CampaignOption);
        if (inWa) waValue.push(c as CampaignOption);
        if (!inEmail && !inWa) {
            const shown: CampaignOption = c ?? { CampaignID: id, CampaignName: '' };
            if (twoPickers && id < 0) waValue.push(shown); else emailValue.push(shown);
        }
    });

    // onChange of ONE picker. Its domain is its options plus the fallback chips it shows; the new
    // selection is (CampaignIDs minus that domain) ∪ what the picker now holds. So ids outside the
    // domain — the other picker's sends, an `#id` shown in the other picker — are preserved, and a
    // combined send leaves BOTH views when it is un-ticked in either. Existing order is kept and
    // new picks are appended, as the single picker did. No cap here, exactly as before (the single
    // picker had none; only the source action refuses CAMPAIGN_IDS_MAX in words), and 0 /
    // duplicates are dropped where they always were — at the wire boundary, `toSendSearchRequest`.
    const pickInto = (options: CampaignOption[], shown: CampaignOption[]) => (next: CampaignOption[]) => {
        const domain = new Set(options.concat(shown).map((c) => c.CampaignID));
        const picked = next.map((c) => c.CampaignID);
        const pickedSet = new Set(picked);
        const kept = selectedIds.filter((id) => !domain.has(id) || pickedSet.has(id));
        const keptSet = new Set(kept);
        onChange({ CampaignIDs: kept.concat(picked.filter((id) => !keptSet.has(id))) });
    };

    // The chips row under the bar (panel closed): one chip per selected send. Label = the picker's
    // label for that send; marker = the send's channels. For a key the catalog does not list, the
    // marker is said only where the KEY proves it: negative ⇒ WhatsApp-only by definition. A
    // positive key proves e-mail but not the absence of WhatsApp, so it gets no marker rather than
    // an "אימייל" that would read as "e-mail only".
    const sendChip = (id: number): { name: string; marker: string } => {
        const c = campaignById.get(id);
        if (c) return { name: twoPickers && c.HasEmail !== true ? waLabelOf(c) : emailLabelOf(c), marker: markerOf(c) };
        const ghost: CampaignOption = { CampaignID: id, CampaignName: '' };
        return twoPickers && id < 0
            ? { name: waLabelOf(ghost), marker: t(channelFilterKey(eSendSearchChannel.WHATSAPP)) }
            : { name: emailLabelOf(ghost), marker: '' };
    };
    // De-duplicated for display only (React keys); the stored list is not rewritten here.
    const chipIds = selectedIds.filter((id, i, arr) => arr.indexOf(id) === i);

    // What a picker says when it has no option to offer — and, when the panel has nothing at all to
    // show, what the disabled "עוד מסננים" button says. The three states are different claims.
    const campaignsEmptyText: string = campaignsError
        ? t(`${SS}campaign.loadFailed`)
        : (campaignsLoading ? t(`${SS}campaign.loading`) : t(`${SS}campaign.none`));

    // ── "mark the campaigns that sent from source X" ──────────────────────────────────────────
    // THE GATE, and it has three arms rather than one. The map must exist (script 54 ran), the
    // campaign list must have loaded, and it must not have failed — because this action ticks
    // CAMPAIGN IDS, and ticking ids the picker cannot name produces bare `#4821` chips against a
    // search the operator then cannot read back. Same doctrine as the grid's source line: never
    // act on a list we could not load.
    const srcMenuAvailable = sourceMapAvailable === true
        && !campaignsError
        && campaigns.length > 0
        && (sourceOptions ?? []).length > 0;

    const [srcAnchor, setSrcAnchor] = useState<any>(null);
    // Component state, DELIBERATELY not part of `Filters`: buildExportCriteria scans the live
    // filter object and would print any unknown key into the export's criteria block as a
    // criterion — a source claim the 14 exported columns contain nothing to corroborate.
    //
    // It is retired by the effect below, and by nothing else. Do not describe it as "transient" or
    // as "wiped by the next filter change": an earlier version of this very comment said exactly
    // that while nothing in the file wiped it, and under Immer a date or text patch leaves
    // `CampaignIDs`' reference intact, so "the next filter change" was never the trigger anyway.
    // `after` is the selection the action PRODUCED. It is what makes the notice self-invalidating:
    // the effect below drops the whole notice the moment the live selection stops matching it.
    //
    // 🔴 THAT EFFECT IS NOT POLISH. Without it the notice was component state with no writer but its
    // own Undo, so it outlived everything: after "נקה הכל" the sentence still read "סומנו 37
    // קמפיינים ששלחו ממקור X" while CampaignIDs had become [] — which SendSearch.ts documents as
    // ALL CAMPAIGNS. limitTags={2} collapses the chips to "+35", so that sentence was the only
    // human-readable statement of scope on the screen (FILTERS-2PICK 2026-10-02: the chips row
    // now lists every selected send while the panel is closed, but the notice is still the only
    // statement of WHICH SOURCE produced them), and it was asserting one source over a
    // result set covering the entire sub-account. On an audit grid that is the exact failure the
    // whole screen exists to prevent, and it ran in the dangerous direction.
    const [srcNotice, setSrcNotice] = useState<
        { text: string; undo: number[] | null; after: number[] } | null
    >(null);

    // Campaign ids attributable to a source, intersected with what the picker can actually show.
    // The server builds the pair set from the same #Camp the campaign list comes from, so this
    // intersection should be a no-op — it is here because "should be" is not "is", and a pair the
    // picker cannot render is exactly the bare-`#id` chip this action must never create.
    const campaignIdsForSource = (dsId: number): number[] => {
        const known = new Set(campaigns.map((c) => c.CampaignID));
        const out: number[] = [];
        (sourceCampaigns ?? []).forEach((p) => {
            if (p.DataSourceID === dsId && known.has(p.CampaignID) && out.indexOf(p.CampaignID) < 0) {
                out.push(p.CampaignID);
            }
        });
        return out;
    };

    const applySourceExpansion = (s: SendSearchCatalogSource) => {
        setSrcAnchor(null);
        const name = s.DataSourceName || t(`${SS}source.nameNotFound`);
        const expansion = campaignIdsForSource(s.DataSourceID);

        // REFUSAL BAND 1 — zero yield. This is not a courtesy message, it is a correctness guard:
        // an EMPTY CampaignIDs array means ALL CAMPAIGNS, so dispatching a zero-yield expansion
        // would turn a narrowing click into "show me the entire account" while the operator
        // believes they just filtered to one source.
        if (expansion.length === 0) {
            setSrcNotice({ text: t(`${SS}source.markNone`, { name, id: s.DataSourceID }), undo: null, after: [] });
            return;
        }

        const prev = value.CampaignIDs || [];
        const added = expansion.filter((id) => prev.indexOf(id) < 0);

        // NO-OP GUARD. Clicking the same source twice — or a second source whose campaigns are
        // already ticked — used to dispatch an identical selection and store `undo: prev` where
        // `prev` WAS the current selection. The Undo button then dismissed the only sentence
        // describing the scope while un-ticking nothing, and each redundant click cost two full
        // POST /Search round-trips over a 395-million-row SP. Refuse, say so, offer no Undo.
        if (added.length === 0) {
            setSrcNotice({
                text: t(`${SS}source.markAlready`, { count: expansion.length, name, id: s.DataSourceID }),
                undo: null,
                after: [],
            });
            return;
        }

        const union = prev.concat(added);

        // REFUSAL BAND 2 — over the server's cap. The server answers 400 DATA_INCORRECT, which the
        // screen paints as a generic "load failed" banner, so the operator would be told the search
        // broke rather than that they asked for too much. Refuse here, name both numbers, and leave
        // the existing ticks exactly as they were.
        if (union.length > CAMPAIGN_IDS_MAX) {
            setSrcNotice({
                text: t(`${SS}source.markTooMany`, { name, count: union.length, max: CAMPAIGN_IDS_MAX }),
                undo: null,
                after: [],
            });
            return;
        }

        // ONE dispatch. Two would be two identical POST /Search — the double-fetch class already
        // fixed on this screen once.
        onChange({ CampaignIDs: union });
        setSrcNotice({
            // `added.length`, not `expansion.length`: the sentence must count what THIS CLICK did.
            // Reporting the source's full size after a partial expansion would name a number the
            // operator cannot reconcile with the chips.
            text: t(`${SS}source.markDone`, { count: added.length, name, id: s.DataSourceID }),
            undo: prev,
            after: union,
        });
    };

    // ── advanced panel ────────────────────────────────────────────────────────────────────────
    const advFields: SendSearchField[] = fields ?? [];
    const advRules: SendSearchFilterRule[] = rules ?? [];
    const advSort: SendSearchSort = sort ?? defaultSendSearchSort();
    // The builder is rendered only when the server actually offered fields AND the screen wired a
    // change handler: a builder that can express nothing is worse than none, because the user
    // concludes the FILTER found nothing rather than that the field list is absent.
    const advAvailable = advFields.length > 0 && !!onRulesChange;
    // FILTERS-2PICK 2026-10-02: the panel opens when EITHER part has something to show — the
    // campaign pickers (a list arrived, or something is selected that must stay reachable) or the
    // builder. Gating it on the fields alone, as before, would make the campaign filter unreachable
    // on a server that offers no searchable field.
    const panelAvailable = advAvailable || campaigns.length > 0 || selectedIds.length > 0;
    const [advOpen, setAdvOpen] = useState(false);
    // What the screen actually shows: a panel that became unavailable (e.g. the dates moved to a
    // window with no sends) counts as CLOSED, so the badge and the chips row come back instead of
    // the filters vanishing with it.
    const panelOpen = advOpen && panelAvailable;
    // COUNT = complete rules only. An incomplete rule is not sent (see `isRuleComplete`), so
    // counting it would tell the user a filter is active that is not.
    // 11-sendsearch §8: and only APPLIED rules — the badge and the chips describe the grid, and the
    // grid is filtered by what the last request carried, never by the builder's unapplied edits.
    const shownSort: SendSearchSort = appliedSort ?? advSort;
    const activeRules = completeRules(appliedRules ?? advRules, advFields);
    // A sort on a hidden field is also an active, invisible modification of what the grid shows, so
    // it is counted alongside the rules — otherwise a user who collapses the panel sees a reordered
    // grid with a badge reading 0.
    const advCount = activeRules.length + (shownSort.FieldKey ? 1 : 0);
    // FILTERS-2PICK 2026-10-02: + 1 when any send is selected. The campaign selection is ONE filter
    // (however many sends it holds), and the "{{count}} מסננים פעילים" sentence counts filters;
    // the sends themselves are listed one chip each in the chips row.
    // The rules/sort part counts only while the builder is available: their chips are gated on
    // `advAvailable`, and the sentence must never count a filter the row does not show.
    const activeCount = (advAvailable ? advCount : 0) + (selectedIds.length > 0 ? 1 : 0);

    // Local text state: typing must not refetch. Committed by "חפש" or Enter — the same
    // searchInput/doSearch split DataSources.tsx and SmartSendManageTab.tsx:60,102 use.
    const [text, setText] = useState(value.SearchText);
    // Re-sync when the slice's text changes from the outside (e.g. "נקה הכל"), otherwise the box would
    // keep showing a term that is no longer being filtered on.
    useEffect(() => { setText(value.SearchText); }, [value.SearchText]);

    // THE NOTICE IS ONLY TRUE WHILE THE SELECTION IT DESCRIBES IS STILL THE SELECTION.
    // Compared by VALUE, not by reference: `value.CampaignIDs` is rebuilt by the reducer on every
    // patch, so an identity check would clear the notice immediately and a length check would miss
    // a same-size swap. Order-insensitive because the picker rewrites the array on every tick.
    // A refusal notice (undo === null) describes no selection, so it survives until the next action
    // or an explicit dismiss — it is a message about a click that did NOT happen.
    useEffect(() => {
        if (!srcNotice) return;
        // A REFUSAL notice (undo === null) describes a click that changed NOTHING, so there is no
        // selection to compare it against — and the first version of this effect returned early on
        // it, which left "…would reach 700 campaigns, the maximum is 500, narrow the date range" on
        // screen for the life of the mount. That reproduced the very defect this effect was added
        // to fix, in the one branch it skipped, and it was worse: the message instructs an action
        // (narrow the dates) which by itself could never clear it. Any change to the filters it
        // referred to retires it.
        if (srcNotice.undo === null) { setSrcNotice(null); return; }
        const now = value.CampaignIDs || [];
        const same = now.length === srcNotice.after.length
            && srcNotice.after.every((id) => now.indexOf(id) >= 0);
        if (!same) setSrcNotice(null);
        // The date/range fields are dependencies for the REFUSAL branch, not the success branch:
        // under Immer a date patch leaves `CampaignIDs`' reference intact, so keying on the ids
        // alone would never fire and a refusal would outlive the range that produced it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value.CampaignIDs, value.DateFrom, value.DateTo, value.IncludeOverOneYear, value.RowKind]);

    const commitText = () => {
        // FIX 2026-08-09: fire ONE fetch per click, not two. onChange -> setFilters makes the
        // `filters` effect in SendSearchPanel refetch on a committed change; calling onSearch()
        // as well produced two identical POST /Search calls per "חפש"/Enter — which, under the
        // (previously) named PK_Match constraint on #Match, also raced into "There is already an
        // object named 'PK_Match'". onSearch() is only needed when the text did NOT change, so the
        // effect will not fire on its own.
        const next = text.trim();
        if (next !== value.SearchText) onChange({ SearchText: next });
        else onSearch();
    };

    // Dates are exchanged as ISO `yyyy-MM-dd` — the value shape a native date input uses and a shape
    // JSON.NET binds straight into `DateTime?`. An empty input yields '', which must travel as null
    // (the SP's @prm_DateFrom/@prm_DateTo default), not as an empty string.
    // QA BUG-05 (F-07): the native input reports each digit of the year as a whole date (typing 2026 yields
    // 0002, 0020, 0202, 2026), so the field shows what is typed (local state, like `text` above) and the
    // filter is committed only for an emptied field or a 4-digit year from 2000-01-01 (no SmartSend send
    // predates it; 1753, the SQL `datetime` floor of SendSearchLogic.IsSqlDateSafe, still let 1902/1920
    // through), and only when the date changed. A field with one segment deleted also reports '' — with
    // `validity.badInput` — and is NOT a cleared field: it commits nothing (BUG-05-r1).
    // QA BUG-05-r2: NOT on every complete value — retyping the day of a full date passes through another
    // complete date (day «3» of «30» is 03), and a full keyboard clear fires `input` only for its FIRST
    // deletion (badInput), never for the last one that leaves the field truly empty. So the field is
    // committed once it SETTLES: DATE_SETTLE_MS after its last input or key (keyup also arms it, for the
    // deletions that fire no `input`; later while a pointer is pressed, QA BUG-05-r4 `armDate`), or at once
    // on Enter. The commit reads the ELEMENT, not the last
    // event, so a clear that ended with no event is still seen; a field left incomplete on blur / Enter
    // goes back to the committed date, so it never shows a date that is not the filter.
    // QA BUG-05-r3: blur does NOT commit. A click on another control blurs the field first, and a commit
    // there started the Search whose loading backdrop then took the mouseup — the click («סוכנים בלבד»,
    // «נקה הכל») was lost. So the click runs first and the armed timer commits the date after it. For that,
    // the re-sync below leaves a field ALONE while its timer is armed: a change of another filter (role,
    // channel, campaigns…) must not wipe a date that is still settling. «נקה הכל» in this bar drops the
    // armed timers first (`onClearAllClick`), so it still clears a date that was typed but never committed.
    // Otherwise re-synced on ANY committed change of `value`. D+1 (exclusiveUpperBound) is untouched.
    const [dateFrom, setDateFrom] = useState(value.DateFrom ?? '');
    const [dateTo, setDateTo] = useState(value.DateTo ?? '');
    // The settle timer outlives the render that armed it: it reads the CURRENT filters and onChange.
    const latest = useRef({ value, onChange });
    latest.current = { value, onChange };
    const dateEl = useRef<{ [w in 'DateFrom' | 'DateTo']: HTMLInputElement | null }>({ DateFrom: null, DateTo: null });
    // undefined = no timer armed for that field (set back on every clear / fire).
    const dateTimer = useRef<{ [w in 'DateFrom' | 'DateTo']?: number }>({});
    useEffect(() => {
        if (dateTimer.current.DateFrom === undefined) setDateFrom(value.DateFrom ?? '');
        if (dateTimer.current.DateTo === undefined) setDateTo(value.DateTo ?? '');
    }, [value]);
    useEffect(() => () => {
        window.clearTimeout(dateTimer.current.DateFrom);
        window.clearTimeout(dateTimer.current.DateTo);
    }, []);
    // QA BUG-05-r4: is a pointer pressed anywhere on the page? Without this the settle timer fired 800ms
    // after the last key whatever the hand was doing; a commit inside a click (pointerdown … pointerup)
    // started the Search whose loading backdrop (Loader, a MUI Backdrop at z-index 2300) then took the
    // mouseup, and the click was lost. For a MOUSE, pointerup, mouseup and click are dispatched in ONE task,
    // so a timer that waits for the pointerup runs after the click (a touch tap's click comes from a later
    // gesture event, so there it is not guaranteed). Window CAPTURE for the pointer events (nothing can stop them
    // first). The window `blur` (focus left the browser: a pointerup may never arrive) is NOT a capture
    // listener and checks its target — a capturing one would also catch the blur of the date input
    // itself, which happens inside the very pointerdown it must wait for.
    const pointerDown = useRef(false);
    useEffect(() => {
        const down = () => { pointerDown.current = true; };
        const up = () => { pointerDown.current = false; };
        const winBlur = (e: FocusEvent) => { if (e.target === window) pointerDown.current = false; };
        window.addEventListener('pointerdown', down, true);
        window.addEventListener('pointerup', up, true);
        window.addEventListener('pointercancel', up, true);
        window.addEventListener('blur', winBlur);
        return () => {
            window.removeEventListener('pointerdown', down, true);
            window.removeEventListener('pointerup', up, true);
            window.removeEventListener('pointercancel', up, true);
            window.removeEventListener('blur', winBlur);
        };
    }, []);
    const clearDateTimer = (which: 'DateFrom' | 'DateTo') => {
        window.clearTimeout(dateTimer.current[which]);
        dateTimer.current[which] = undefined;
    };
    const isCommittable = (el: HTMLInputElement) => {
        const v = el.value;
        const cleared = v === '' && !(el.validity && el.validity.badInput);
        return cleared || (/^\d{4}-\d{2}-\d{2}$/.test(v) && v >= '2000-01-01');
    };
    const revertDate = (which: 'DateFrom' | 'DateTo') => {
        const committed = latest.current.value[which] ?? '';
        if (which === 'DateFrom') setDateFrom(committed); else setDateTo(committed);
    };
    const commitDate = (which: 'DateFrom' | 'DateTo', revertIfIncomplete: boolean) => {
        clearDateTimer(which);
        const el = dateEl.current[which];
        if (!el) return;
        if (isCommittable(el)) {
            const v = el.value;
            if (v !== (latest.current.value[which] ?? '')) latest.current.onChange({ [which]: v ? v : null } as Partial<Filters>);
        } else if (revertIfIncomplete) {
            revertDate(which);
        }
    };
    // Blur: an incomplete field goes back to the committed date (and its timer is dropped); a committable
    // one is left to its armed timer (QA BUG-05-r3).
    const onDateBlur = (which: 'DateFrom' | 'DateTo') => {
        const el = dateEl.current[which];
        if (!el || isCommittable(el)) return;
        clearDateTimer(which);
        revertDate(which);
    };
    // QA BUG-05-r4: a settled date does not commit while a pointer is pressed — it re-arms in short steps
    // (up to DATE_PRESS_MAX_MS of waiting in all) and commits after the pointerup, i.e. after the click.
    // The slot stays set the whole time, so the re-sync guard keeps protecting the field and «נקה הכל»
    // (`onClearAllClick`) also cancels the wait.
    const armDate = (which: 'DateFrom' | 'DateTo', delay = DATE_SETTLE_MS, waited = 0) => {
        clearDateTimer(which);
        dateTimer.current[which] = window.setTimeout(() => {
            if (pointerDown.current && waited < DATE_PRESS_MAX_MS) {
                armDate(which, DATE_PRESS_STEP_MS, waited + DATE_PRESS_STEP_MS);
                return;
            }
            commitDate(which, false);
        }, delay);
    };
    const onDate = (which: 'DateFrom' | 'DateTo') => (e: any) => {
        const v: string = e.target.value;
        if (which === 'DateFrom') setDateFrom(v); else setDateTo(v);
        armDate(which);
    };
    const dateInputProps = (which: 'DateFrom' | 'DateTo') => ({
        inputRef: (el: HTMLInputElement | null) => { dateEl.current[which] = el; },
        onBlur: () => onDateBlur(which),
        onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter') commitDate(which, true); },
        onKeyUp: (e: React.KeyboardEvent) => { if (e.key !== 'Enter' && e.key !== 'Tab') armDate(which); },
    });
    // «נקה הכל» drops a date that is still settling: the reset must clear it, not have its timer commit
    // it right after (QA BUG-05-r3).
    const onClearAllClick = () => {
        clearDateTimer('DateFrom');
        clearDateTimer('DateTo');
        onClearAll();
    };

    return (
        <Box
            style={{
                background: 'var(--pls-d-surface, #fff)', border: '1px solid var(--pls-d-border, #e0e0e0)', borderRadius: 10,
                padding: 15, marginBottom: 13,
            }}
        >
            {/* QA BUG-21: row 1 and the campaign pickers share ONE box, as wide as row 1 on one line
                (fit-content) and never wider than the card (maxWidth 100%). The pickers' row takes that
                width without adding to it, so the WhatsApp picker ends exactly under «עוד מסננים» in
                every language; once row 1 wraps, the box is the card width, as before. No measuring. */}
            <Box style={{ width: 'fit-content', maxWidth: '100%' }}>
            {/* ── row 1: text · source mark · dates · actions ── */}
            <Box style={{ display: 'flex', gap: 9, flexWrap: 'wrap', alignItems: 'center' }}>
                <TextField
                    variant="outlined"
                    size="small"
                    placeholder={t(`${SS}searchPlaceholder`)}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitText(); }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                    }}
                    // QA BUG-21: `width` equal to the basis. The box around row 1 sizes itself from each
                    // item's width, not its flex basis, and would count this field below 380 and wrap the
                    // row. Its size in the row is unchanged: the basis still decides.
                    style={{ minWidth: 330, flex: '0 1 380px', width: 380 }}
                    inputProps={{ 'aria-label': t(`${SS}searchPlaceholder`), autoComplete: 'off' }}
                />

                {/* RoleFilter dropdown REMOVED 2026-08-09. It was a search-text SCOPE ("search agent
                    identity" vs "search supervisor value"), NOT a row filter — inert unless a search
                    term was typed (SP 21_SendSearch_SearchSends_SP.sql:532 wraps its whole clause in
                    `@Search IS NULL OR …`), so on an empty box it changed nothing and read as a broken
                    control. Its options ("סוכן בלבד"/"מפקח בלבד") also duplicated, to the eye, the
                    RowKind segmented control below ("סוכנים בלבד"/"מפקחים בלבד"). In this domain
                    מפקח ≡ נמען-ריכוז, so RowKind already gives the agent/supervisor split the operator
                    wants. `RoleFilter` stays in state at its default eRoleFilter.All (0) and is still
                    sent to the SP unchanged, so text search now always spans BOTH agent and supervisor
                    — the SP's own default. Nothing on the wire or in the SP changed. */}

                {/* The campaign multi-select that stood here is the first row of the "עוד מסננים"
                    panel since FILTERS-2PICK 2026-10-02 (Idan): two pickers, e-mail campaigns and
                    WhatsApp campaigns — see `SendPicker` and the panel under this row. */}

                {/* ── "סמן קמפיינים ששלחו ממקור …" ────────────────────────────────────────────
                    In the TOP ROW (FILTERS-2PICK 2026-10-02 kept it here when the pickers moved into
                    the panel), never a header inside a picker's popup. A groupBy header in MUI v4 is
                    not an option — not focusable, not selectable — so putting it there means
                    rewriting useAutocomplete. What it ticks is visible either way: in the pickers
                    when the panel is open, and as one chip per send under the bar when it is closed.

                    The LABEL IS A VERB naming the derivation. Not "מקור:" — a noun beside the
                    campaign filter promises a filter, and the moment the user reads it as a filter
                    they will expect the grid to narrow to that source and nothing else. What this
                    does is tick campaigns; the label has to say so.

                    Rendered only when the gate holds. Not disabled — ABSENT. A disabled control
                    invites the question "why", and the honest answer ("the server has not had
                    script 54 run") is not something to put in a tooltip on an auditor's screen. */}
                {srcMenuAvailable && (
                    <>
                        <Button
                            size="small"
                            variant="outlined"
                            onClick={(e: any) => setSrcAnchor(e.currentTarget)}
                            // QA BUG-20 (Idan): the 40px of the outlined inputs beside it (the search box and the
                            // dates, TextField size="small"); size="small" alone is ~31px. This control only, no theme change.
                            style={{ whiteSpace: 'nowrap', flex: '0 0 auto', height: 40 }}
                        >
                            {t(`${SS}source.markButton`)}
                        </Button>
                        <Menu
                            anchorEl={srcAnchor}
                            open={!!srcAnchor}
                            onClose={() => setSrcAnchor(null)}
                            // Same portal/direction problem as every other popup on this screen.
                            PaperProps={{ dir: isRTL ? 'rtl' : 'ltr', style: { maxHeight: 360 } }}
                            getContentAnchorEl={null}
                            anchorOrigin={{ vertical: 'bottom', horizontal: isRTL ? 'right' : 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: isRTL ? 'right' : 'left' }}
                        >
                            {(sourceOptions ?? []).map((s) => (
                                <MenuItem key={s.DataSourceID} onClick={() => applySourceExpansion(s)}>
                                    <Box style={{ display: 'flex', alignItems: 'baseline', gap: 8, width: '100%' }}>
                                        {/* first-strong isolation: a Hebrew name renders RTL, an
                                            "AgentPortfolio_Q3" renders LTR, and neither drags the
                                            id to the wrong end. Same treatment the grid's source
                                            line uses. */}
                                        <bdi style={{
                                            flex: '1 1 auto', minWidth: 0, overflow: 'hidden',
                                            textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 13.5,
                                        }}>
                                            {s.DataSourceName === '' || s.DataSourceName == null
                                                ? t(`${SS}source.nameNotFound`)
                                                : s.DataSourceName}
                                        </bdi>
                                        {/* Deleted sources are LISTED, not hidden: a send that went
                                            out from a source deleted afterwards still went out from
                                            it, and dropping it here would erase evidence. */}
                                        {s.IsDeleted && (
                                            <Chip
                                                size="small"
                                                label={t(`${SS}source.deletedChip`)}
                                                style={{ flex: '0 0 auto', height: 18, fontSize: 11, borderRadius: 3 }}
                                                variant="outlined"
                                            />
                                        )}
                                        {/* The id is ALWAYS shown. Two live sources may legally
                                            carry the same name — the unique index on the name is
                                            filtered to IsDeleted = 0 — so the name alone cannot
                                            disambiguate and the operator must see which one they
                                            are about to expand. */}
                                        <span dir="ltr" style={{
                                            flex: '0 0 auto', direction: 'ltr', unicodeBidi: 'isolate',
                                            fontSize: 11.5, color: 'var(--pls-d-text-2, #5b6b7b)', fontVariantNumeric: 'tabular-nums',
                                        }}>{`#${s.DataSourceID}`}</span>
                                        {/* The size of the expansion, BEFORE committing to it. */}
                                        <span style={{ flex: '0 0 auto', fontSize: 11.5, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                                            {t(`${SS}source.markCount`, { count: s.CampaignCount })}
                                        </span>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Menu>
                    </>
                )}

                <TextField
                    type="date"
                    variant="outlined"
                    size="small"
                    label={t(`${SS}dateFrom`)}
                    value={dateFrom}
                    onChange={onDate('DateFrom')}
                    {...dateInputProps('DateFrom')}
                    InputLabelProps={{ shrink: true }}
                    style={{ width: 160 }}
                />
                <TextField
                    type="date"
                    variant="outlined"
                    size="small"
                    label={t(`${SS}dateTo`)}
                    value={dateTo}
                    onChange={onDate('DateTo')}
                    {...dateInputProps('DateTo')}
                    InputLabelProps={{ shrink: true }}
                    style={{ width: 160 }}
                />

                {/* QA BUG-20-r1: the whole row at the 40px of the outlined inputs (this file only, no theme). */}
                <Button variant="contained" color="primary" disabled={loading} onClick={commitText} style={{ height: 40 }}>
                    {t(`${SS}search`)}
                </Button>
                {/* "עוד מסננים ▾" — was a DISABLED stub in V1 (`Mock-v3:183` has no handler and no
                    panel). It toggles the panel under this row: the campaign pickers (FILTERS-2PICK
                    2026-10-02) and, when the server offers searchable fields, the advanced builder.
                    Disabled only when NEITHER part has anything to show (`panelAvailable`), and the
                    tooltip then says why — the campaign list's state, which is what empties both —
                    so a disabled button is never mistaken for a broken one.
                    The Tooltip wraps a <span>, not the Button: MUI attaches its listeners to the
                    child, a disabled button fires no pointer events, and the tooltip that explains
                    WHY it is disabled would be the one tooltip that never appears. */}
                <Tooltip title={(panelAvailable ? t(`${SS}moreFilters`) : campaignsEmptyText) as string}>
                    <span>
                        <Badge
                            color="primary"
                            badgeContent={activeCount}
                            // Hidden while the panel is OPEN: the pickers and the rules are on screen
                            // and counting them twice is noise. Collapsed, the badge and the chips row
                            // are what show that the grid is narrowed by something inside the panel.
                            invisible={panelOpen || activeCount === 0}
                        >
                            <Button
                                variant="outlined"
                                disabled={!panelAvailable}
                                aria-expanded={panelOpen}
                                onClick={() => setAdvOpen((v) => !v)}
                                endIcon={panelOpen ? <ExpandLess /> : <ExpandMore />}
                                // QA BUG-20-r1: the row's 40px, like «חפש» and «סמן קמפיינים לפי מקור».
                                style={{ height: 40 }}
                            >
                                {t(`${SS}moreFilters`)}
                            </Button>
                        </Badge>
                    </span>
                </Tooltip>
            </Box>

            {/* ── the "עוד מסננים" panel — DIRECTLY UNDER the top row (FILTERS-2PICK 2026-10-02;
                it used to sit under row 2) ─────────────────────────────────────────────────────
                INSIDE the same card, with the SAME dashed separator treatment as row 2 — part of one
                filter, not a second filter mechanism. A separate popover would let the user see the
                grid without the filters that are narrowing it.
                First row: the campaign pickers, which apply immediately on every tick (as the single
                picker did). Under them, the advanced builder exactly as before — it has its own
                "החל מסננים", and its "נקה מסננים מתקדמים" still clears rules only; "נקה הכל" in
                row 2 clears everything, the campaign selection included. */}
            <Collapse in={panelOpen} timeout="auto" unmountOnExit>
                <Box
                    style={{
                        display: 'flex', gap: 9, flexWrap: 'wrap', alignItems: 'center',
                        marginTop: 12, paddingTop: 11, borderTop: '1px dashed var(--pls-d-border, #e0e0e0)',
                        // QA BUG-21: exactly the width of the box around row 1, and adds nothing to it
                        // (width 0 + minWidth 100%), so row 1 alone sets that width.
                        width: 0, minWidth: '100%',
                    }}
                >
                    {twoPickers ? (
                        <>
                            <SendPicker
                                label={t(`${SS}campaign.emailLabel`)}
                                // "all" ONLY when the WHOLE selection is empty: a WhatsApp pick
                                // narrows the grid to the picked sends, so an empty e-mail picker
                                // beside it does NOT mean "all e-mail campaigns".
                                placeholder={selectedIds.length === 0
                                    ? t(`${SS}campaign.emailAll`)
                                    : t(`${SS}campaign.emailPick`)}
                                noOptionsText={campaignsEmptyText}
                                options={emailOptions}
                                value={emailValue}
                                labelOf={emailLabelOf}
                                markerOf={markerOf}
                                onPick={pickInto(emailOptions, emailValue)}
                                isRTL={!!isRTL}
                            />
                            <SendPicker
                                label={t(`${SS}campaign.waLabel`)}
                                placeholder={selectedIds.length === 0
                                    ? t(`${SS}campaign.waAll`)
                                    : t(`${SS}campaign.waPick`)}
                                noOptionsText={campaignsEmptyText}
                                options={waOptions}
                                value={waValue}
                                labelOf={waLabelOf}
                                markerOf={markerOf}
                                onPick={pickInto(waOptions, waValue)}
                                isRTL={!!isRTL}
                            />
                        </>
                    ) : (
                        // Not two-channel ready (see `twoPickers`): the single picker with today's
                        // texts — it cannot claim its list is "e-mail" campaigns.
                        <SendPicker
                            label={t(`${SS}campaign.label`)}
                            placeholder={selectedIds.length === 0
                                ? t(`${SS}campaign.all`)
                                : t(`${SS}campaign.search`)}
                            noOptionsText={campaignsEmptyText}
                            options={emailOptions}
                            value={emailValue}
                            labelOf={emailLabelOf}
                            markerOf={markerOf}
                            onPick={pickInto(emailOptions, emailValue)}
                            isRTL={!!isRTL}
                        />
                    )}
                </Box>
            </Collapse>
            </Box>
            {/* QA BUG-21: the builder keeps the full card width, so it is outside the box above, in its
                own Collapse, opened and closed with the pickers' Collapse (same `panelOpen`). */}
            <Collapse in={panelOpen} timeout="auto" unmountOnExit>
                {advAvailable && (
                    <Box style={{ marginTop: 12, paddingTop: 11, borderTop: '1px dashed var(--pls-d-border, #e0e0e0)' }}>
                        <AdvancedFilterBuilder
                            fields={advFields}
                            rules={advRules}
                            onRulesChange={onRulesChange!}
                            sort={advSort}
                            onSortChange={onSortChange ?? (() => undefined)}
                            onApply={onApply ?? onSearch}
                            loading={loading}
                        />
                    </Box>
                )}
            </Collapse>

            {/* ── row 2: segmented row-kind · clear all · >1-year opt-in ── */}
            <Box
                style={{
                    display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
                    marginTop: 12, paddingTop: 11, borderTop: '1px dashed var(--pls-d-border, #e0e0e0)',
                }}
            >
                {/* ── 11-sendsearch §8: ערוץ: [שניהם] [אימייל] [וואטסאפ] ─────────────────────────
                    ONLY when the server says the two-channel search is ready (hidden, not greyed —
                    see `channelsReady`). A committed filter like RowKind beside it: the patch goes
                    through `setFilters`, which resets the page, and the effect refetches once. */}
                {channelsReady === true && (
                    <Box style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Typography component="span" style={{ fontSize: 13, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                            {`${t(`${SS}channel.label`)}:`}
                        </Typography>
                        <ButtonGroup size="small" role="group" aria-label={t(`${SS}channel.label`)}>
                            {SEND_SEARCH_CHANNELS.map((c) => {
                                const on = value.Channel === c;
                                return (
                                    <Button
                                        key={c}
                                        onClick={() => { if (!on) onChange({ Channel: c }); }}
                                        aria-pressed={on}
                                        style={{
                                            background: on ? '#0371AD' : 'var(--pls-d-surface, #fff)',
                                            color: on ? '#fff' : 'var(--pls-d-text, #3b4754)',
                                            fontWeight: on ? 700 : 400,
                                            whiteSpace: 'nowrap',
                                        }}
                                    >
                                        {t(channelFilterKey(c))}
                                    </Button>
                                );
                            })}
                        </ButtonGroup>
                    </Box>
                )}

                <ButtonGroup size="small" role="group" aria-label={t(`${SS}kind.all`)}>
                    {KIND_ORDER.map((k) => {
                        const on = value.RowKind === k;
                        return (
                            <Button
                                key={k}
                                // QA BUG-04 (F-04): the channel buttons' guard (:837) — a click on the pressed
                                // button changes nothing, so it must not send a Search.
                                onClick={() => { if (!on) onChange({ RowKind: k }); }}
                                aria-pressed={on}
                                style={{
                                    // --blue #0371AD when selected (`Mock-v3:58 .seg button.on`).
                                    background: on ? '#0371AD' : 'var(--pls-d-surface, #fff)',
                                    color: on ? '#fff' : 'var(--pls-d-text, #3b4754)',
                                    fontWeight: on ? 700 : 400,
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {t(`${SS}${KIND_KEY[k]}`)}
                            </Button>
                        );
                    })}
                </ButtonGroup>

                <Button variant="text" onClick={onClearAllClick} style={{ color: 'var(--pls-d-blue, #0371AD)', textDecoration: 'underline' }}>
                    {t(`${SS}clearAll`)}
                </Button>

                {/* ── readback for the source expansion ───────────────────────────────────────
                    Why this exists at all: in the open panel the pickers' `limitTags={2}` renders
                    an expansion of 37 campaigns as two chips and "+35", and with the panel closed
                    the chips row lists 37 sends without saying where they came from
                    (FILTERS-2PICK 2026-10-02). Without a sentence saying what just happened, a bulk
                    action is indistinguishable from the screen glitching.

                    `aria-live="polite"` because the visual change (chips appearing) is not
                    announced, and the whole point is that the operator knows a machine acted on
                    their behalf.

                    It never enters `Filters`, and therefore cannot leak into the export's criteria
                    block — where a source claim would be uncorroborable, since none of the 14
                    exported columns carries a data source.

                    🔴 IT IS SELF-INVALIDATING, and that is enforced by the effect near the top of
                    this component, NOT by being "transient". An earlier version of this comment
                    claimed the notice was "wiped by the next filter change" while nothing in the
                    file wiped it — a comment asserting behaviour that did not exist, which is the
                    same class of defect this screen's own review history keeps catching. If you
                    change the notice, change that effect with it.

                    THE REGION IS RENDERED UNCONDITIONALLY, and that is not a style choice. A live
                    region that is INSERTED into the DOM already carrying its text is unreliably
                    announced — NVDA and JAWS commonly miss it, because there was no region present
                    to observe a change in. The Box therefore always exists and only its contents
                    swap. `height:0` when empty so it costs no layout. */}
                <Box
                    aria-live="polite"
                    style={srcNotice
                        ? {
                            flex: '1 1 100%', display: 'flex', alignItems: 'center', gap: 10,
                            fontSize: 12.5, color: 'var(--pls-d-text-2, #5b6b7b)',
                        }
                        : { flex: '0 0 0px', height: 0, overflow: 'hidden' }}
                >
                    {srcNotice && (
                        <>
                        <span>{srcNotice.text}</span>
                        {/* Undo restores the EXACT previous array, not "remove this source's ids":
                            the two differ whenever a campaign was already ticked by hand and also
                            belongs to the source, and silently un-ticking a manual choice is the
                            one thing this action must never do. */}
                        {/* `!== null`, NOT a truthy test. `undo: []` is the ordinary case — nothing
                            was ticked before the click — and `[]` is truthy in JS, which is fine
                            here but only by accident; the explicit null check is what states the
                            actual rule. Restoring `[]` is a CORRECT undo (it puts the picker back
                            to "all campaigns", which is what it was), and it is safe to offer only
                            because the effect above drops this whole notice the moment the
                            selection stops matching what the action produced — so the array being
                            replayed can never be stale. Refusal notices carry undo: null and get
                            no button, because they changed nothing to undo. */}
                        {srcNotice.undo !== null && (
                            <Button
                                size="small"
                                variant="text"
                                style={{ color: 'var(--pls-d-blue, #0371AD)', textDecoration: 'underline', padding: 0, minWidth: 0 }}
                                onClick={() => {
                                    onChange({ CampaignIDs: srcNotice.undo as number[] });
                                    setSrcNotice(null);
                                }}
                            >
                                {t(`${SS}source.markUndo`)}
                            </Button>
                        )}
                        </>
                    )}
                </Box>

                {/* Pinned to the far (logical) end — `margin-inline-start:auto` in the mock (`:195`).
                    `marginInlineStart` is used, not marginLeft, so the pin follows the RTL direction. */}
                <FormControlLabel
                    style={{ marginInlineStart: 'auto', color: 'var(--pls-d-text-2, #5b6b7b)' }}
                    control={
                        <Checkbox
                            color="primary"
                            size="small"
                            checked={value.IncludeOverOneYear}
                            onChange={(e) => onChange({ IncludeOverOneYear: e.target.checked })}
                        />
                    }
                    label={
                        // The label states the COST ("זמן החיפוש ארוך יותר") — it is an opt-in that
                        // widens the query past the 12-month partition (§2.2 @prm_IncludeOverOneYear).
                        <Typography component="span" style={{ fontSize: 13, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                            {t(`${SS}includeOverOneYear`)}
                        </Typography>
                    }
                />
            </Box>

            {/* ── active-filter chips, COLLAPSED ONLY ──────────────────────────────────────────
                When the panel is open the pickers and the rules themselves are the display. When it
                is closed these chips are the only thing standing between the user and a grid that is
                silently narrowed — so they name the SEND and the FIELD, not just "3 filters", and
                each one removes its own send / rule. Rule chips: COMPLETE rules only, because only
                those are actually sent; they keep today's `advAvailable` gate.
                FILTERS-2PICK 2026-10-02: one chip per selected send (the campaign selection moved
                into the panel, so with the panel closed this row is where it is seen), with the
                send's channel marker — a combined send is ONE chip reading "אימייל · וואטסאפ". */}
            {!panelOpen && (selectedIds.length > 0 || (advAvailable && advCount > 0)) && (
                <Box
                    style={{
                        display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center',
                        marginTop: 12, paddingTop: 11, borderTop: '1px dashed var(--pls-d-border, #e0e0e0)',
                    }}
                >
                    <Typography component="span" style={{ fontSize: 12.5, color: 'var(--pls-d-text-2, #5b6b7b)' }}>
                        {t(`${SS}adv.activeCount`, { count: activeCount })}
                    </Typography>
                    {chipIds.map((id) => {
                        const s = sendChip(id);
                        return (
                            <Chip
                                key={`send:${id}`}
                                size="small"
                                // Applies immediately, like un-ticking it in a picker: the filter
                                // effect refetches on the committed change. Removes EVERY copy of the
                                // key, so a duplicate cannot keep the grid narrowed under no chip.
                                onDelete={() => onChange({ CampaignIDs: selectedIds.filter((x) => x !== id) })}
                                label={(
                                    <>
                                        {s.name}
                                        {!!s.marker && (
                                            <span
                                                style={{
                                                    marginInlineStart: 6, fontSize: 11, color: 'var(--pls-d-text-2, #5b6b7b)',
                                                    border: '1px solid #d0d7de', borderRadius: 3,
                                                    padding: '0 4px', whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {s.marker}
                                            </span>
                                        )}
                                    </>
                                )}
                                style={{ backgroundColor: 'var(--pls-d-surface-2, #E7F1F8)', color: 'var(--pls-d-blue, #0e4a6e)', fontSize: 12 }}
                            />
                        );
                    })}
                    {advAvailable && activeRules.map((r) => {
                        const f = fieldByKey(advFields, r.FieldKey);
                        const name = f ? (f.DisplayName || f.FieldKey) : r.FieldKey;
                        // Operator label by KEY CONSTRUCTION from the frozen number — the same
                        // `adv.op.<n>` keys the builder's select uses, so the chip and the rule can
                        // never read differently for the same operator.
                        const op = t(`${SS}adv.op.${r.Operator}`);
                        const val = r.Value2 ? `${r.Value1} – ${r.Value2}` : r.Value1;
                        return (
                            <Chip
                                key={r.Id}
                                size="small"
                                // Removing a chip must REFETCH, not just drop the chip: the rule was
                                // narrowing the grid, and leaving the grid narrowed under a chip
                                // that is gone is the exact "silently misled about what you are
                                // looking at" failure this row exists to prevent.
                                // 11-sendsearch §8: through `onRemoveRule` when the screen tracks an
                                // applied set — it drops THIS rule only, from page 0.
                                onDelete={() => {
                                    if (onRemoveRule) { onRemoveRule(r.Id); return; }
                                    onRulesChange!(advRules.filter((x) => x.Id !== r.Id)); onSearch();
                                }}
                                label={`${name} ${op} ${val}`}
                                style={{ backgroundColor: 'var(--pls-d-surface-2, #E7F1F8)', color: 'var(--pls-d-blue, #0e4a6e)', fontSize: 12 }}
                            />
                        );
                    })}
                    {advAvailable && !!shownSort.FieldKey && (
                        <Chip
                            size="small"
                            onDelete={onClearSort
                                ? onClearSort
                                : (onSortChange
                                    ? () => { onSortChange(defaultSendSearchSort()); onSearch(); }
                                    : undefined)}
                            label={`${t(`${SS}sort.label`)}: ${
                                (fieldByKey(advFields, shownSort.FieldKey)?.DisplayName) || shownSort.FieldKey
                            } · ${t(`${SS}sort.${shownSort.Dir}`)}`}
                            style={{ backgroundColor: 'var(--pls-d-surface, #F2F4F7)', color: 'var(--pls-d-text, #3b4754)', fontSize: 12 }}
                        />
                    )}
                </Box>
            )}
        </Box>
    );
};

export default SendSearchFilters;
